import { useState, useEffect, useCallback, useMemo, useRef } from "react";

/**
 * useAutoAdvancePagination - High-performance bidirectional pagination hook
 * with seamless automatic page flipping both when scrolling DOWN to the bottom
 * and scrolling UP to the top, keeping the DOM lightweight and scrolling 60fps smooth.
 *
 * @param {Array} items              - Full filtered dataset array
 * @param {number} initialPageSize    - Initial rows per page (default: 100)
 * @param {Array} pageSizeOptions     - Selectable page sizes (default: [50, 100, 250, 500])
 * @param {boolean} initialAutoAdvance- Enable auto-advancing on reaching scroll boundaries (default: true)
 */
export function useAutoAdvancePagination({
  items = [],
  initialPageSize = 100,
  pageSizeOptions = [50, 100, 250, 500],
  initialAutoAdvance = true,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [autoAdvance, setAutoAdvance] = useState(initialAutoAdvance);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);

  const tableContainerRef = useRef(null);
  const isAdvancingRef = useRef(false);
  const targetScrollPositionRef = useRef("top"); // "top" or "bottom"

  const totalRows = items.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  // Ensure currentPage stays within valid bounds
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  // Reset to page 1 and clear selection whenever items change (search query, filter, re-fetch)
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIndex(null);
    targetScrollPositionRef.current = "top";
  }, [items]);

  // Sliced page data (only pageSize rows exist in DOM at any time)
  const displayedRows = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safeCurrentPage, pageSize]);

  // Position scroll container correctly when page changes:
  // If moving forward -> start at the top (scrollTop = 0)
  // If moving backward -> land at the bottom (scrollTop = scrollHeight - clientHeight)
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    if (targetScrollPositionRef.current === "bottom") {
      // Use requestAnimationFrame to ensure newly rendered page rows are measured
      requestAnimationFrame(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop =
            tableContainerRef.current.scrollHeight - tableContainerRef.current.clientHeight;
        }
        targetScrollPositionRef.current = "top";
      });
    } else {
      container.scrollTop = 0;
    }
  }, [safeCurrentPage]);

  // Manual page change handler (e.g. clicking page numbers or Next/Prev buttons)
  const handlePageChange = useCallback((newPage) => {
    const targetPage = Math.min(Math.max(1, newPage), totalPages);
    targetScrollPositionRef.current = targetPage < safeCurrentPage ? "bottom" : "top";
    setCurrentPage(targetPage);
    setSelectedIndex(null);
  }, [totalPages, safeCurrentPage]);

  // Page size change handler
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setSelectedIndex(null);
    targetScrollPositionRef.current = "top";
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, []);

  const toggleAutoAdvance = useCallback(() => {
    setAutoAdvance((prev) => !prev);
  }, []);

  // Continuous Auto-Advance on scrollbar scroll (dragging thumb to bottom)
  const handleScroll = useCallback(
    (e) => {
      if (!autoAdvance || isAdvancingRef.current) return;
      const target = e.currentTarget;
      if (!target) return;
      const { scrollTop, clientHeight, scrollHeight } = target;

      // Reached near bottom via scrollbar drag
      if (scrollHeight - (scrollTop + clientHeight) <= 6) {
        if (safeCurrentPage < totalPages) {
          isAdvancingRef.current = true;
          setIsFlipping(true);
          targetScrollPositionRef.current = "top";

          setCurrentPage((prev) => Math.min(totalPages, prev + 1));
          setSelectedIndex(0);

          setTimeout(() => {
            isAdvancingRef.current = false;
            setIsFlipping(false);
          }, 450);
        }
      }
    },
    [autoAdvance, safeCurrentPage, totalPages]
  );

  // Bidirectional Auto-Flip on Mouse Wheel / Trackpad Scroll (Both DOWN and UP)
  const handleWheel = useCallback(
    (e) => {
      if (!autoAdvance || isAdvancingRef.current) return;
      const target = tableContainerRef.current;
      if (!target) return;

      const { scrollTop, clientHeight, scrollHeight } = target;
      const isAtBottom = scrollHeight - (scrollTop + clientHeight) <= 12;
      const isAtTop = scrollTop <= 2;

      // 1. Scrolling DOWN at the bottom -> Flip to NEXT page (land at top of next page)
      if (e.deltaY > 0 && isAtBottom) {
        if (safeCurrentPage < totalPages) {
          isAdvancingRef.current = true;
          setIsFlipping(true);
          targetScrollPositionRef.current = "top";

          setCurrentPage((prev) => Math.min(totalPages, prev + 1));
          setSelectedIndex(0);

          setTimeout(() => {
            isAdvancingRef.current = false;
            setIsFlipping(false);
          }, 450);
        }
      }
      // 2. Scrolling UP at the top -> Flip to PREVIOUS page (land at bottom of previous page)
      else if (e.deltaY < 0 && isAtTop) {
        if (safeCurrentPage > 1) {
          isAdvancingRef.current = true;
          setIsFlipping(true);
          targetScrollPositionRef.current = "bottom";

          setCurrentPage((prev) => Math.max(1, prev - 1));
          setSelectedIndex(pageSize - 1);

          setTimeout(() => {
            isAdvancingRef.current = false;
            setIsFlipping(false);
          }, 450);
        }
      }
    },
    [autoAdvance, safeCurrentPage, totalPages, pageSize]
  );

  // Keyboard navigation across rows and pages (both UP and DOWN)
  const handleKeyDown = useCallback(
    (e) => {
      if (!displayedRows.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (prev === null) return 0;
          if (prev < displayedRows.length - 1) {
            return prev + 1;
          }
          // At bottom row of page -> auto-advance to next page
          if (safeCurrentPage < totalPages) {
            targetScrollPositionRef.current = "top";
            setCurrentPage(safeCurrentPage + 1);
            return 0;
          }
          return prev;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (prev === null) return 0;
          if (prev > 0) {
            return prev - 1;
          }
          // At top row of page -> auto-retreat to previous page
          if (safeCurrentPage > 1) {
            targetScrollPositionRef.current = "bottom";
            setCurrentPage(safeCurrentPage - 1);
            return pageSize - 1;
          }
          return 0;
        });
      } else if (e.key === "PageDown") {
        e.preventDefault();
        if (safeCurrentPage < totalPages) {
          targetScrollPositionRef.current = "top";
          setCurrentPage(safeCurrentPage + 1);
          setSelectedIndex(0);
        }
      } else if (e.key === "PageUp") {
        e.preventDefault();
        if (safeCurrentPage > 1) {
          targetScrollPositionRef.current = "bottom";
          setCurrentPage(safeCurrentPage - 1);
          setSelectedIndex(0);
        }
      } else if (e.key === "Home") {
        e.preventDefault();
        targetScrollPositionRef.current = "top";
        setCurrentPage(1);
        setSelectedIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        targetScrollPositionRef.current = "bottom";
        setCurrentPage(totalPages);
        setSelectedIndex(displayedRows.length - 1);
      }
    },
    [displayedRows.length, safeCurrentPage, totalPages, pageSize]
  );

  // Auto-scroll selected row into view when navigating via arrow keys
  useEffect(() => {
    if (selectedIndex === null || !tableContainerRef.current) return;
    const container = tableContainerRef.current;
    const selectedElem = container.querySelector(`[data-row-index="${selectedIndex}"]`);
    if (selectedElem) {
      selectedElem.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex]);

  return {
    currentPage: safeCurrentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    setAutoAdvance,
    toggleAutoAdvance,
    isFlipping,
  };
}

export default useAutoAdvancePagination;
